import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Assessment, ExportFormat, ExportRecord, Paginated } from '@grc/types';
import { apiFetch, apiUrl, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Field,
  LoadingPane,
  PageHeader,
  Select,
  Table,
  formatDateTime,
  useToast,
} from '../../components/ui.js';

const FORMAT_OPTIONS: Array<{ value: ExportFormat; label: string; hint: string }> = [
  { value: 'pdf', label: 'PDF', hint: 'Flowed text document generated on the server.' },
  { value: 'html', label: 'HTML (print-ready)', hint: 'Best fidelity — print to PDF from your browser.' },
  { value: 'docx', label: 'Word (DOCX)', hint: 'Editable Word document with headings and tables.' },
  { value: 'markdown', label: 'Markdown', hint: 'Portable text source.' },
  { value: 'csv', label: 'CSV', hint: 'One row per question with statuses — for spreadsheets.' },
  { value: 'json', label: 'JSON', hint: 'Full structured data for integrations.' },
  { value: 'zip', label: 'ZIP evidence bundle', hint: 'Documents plus every evidence file with a manifest.' },
];

export function ExportsPage() {
  const { org } = useOrg();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [params] = useSearchParams();

  const assessments = useQuery({
    queryKey: ['assessments', org.orgId],
    queryFn: () => apiFetch<Assessment[]>(`/orgs/${org.orgId}/assessments`),
  });
  const history = useQuery({
    queryKey: ['exports', org.orgId],
    queryFn: () =>
      apiFetch<Paginated<ExportRecord>>(`/orgs/${org.orgId}/exports`, { query: { limit: 50 } }),
  });

  const [assessmentId, setAssessmentId] = useState(params.get('assessment') ?? '');
  const [format, setFormat] = useState<ExportFormat>('pdf');

  const generate = useMutation({
    mutationFn: () =>
      apiFetch<ExportRecord>(`/assessments/${assessmentId}/exports`, {
        method: 'POST',
        body: { format },
      }),
    onSuccess: () => {
      toast('Export generated — download it below.', 'success');
      void queryClient.invalidateQueries({ queryKey: ['exports', org.orgId] });
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Export failed', 'error'),
  });

  if (assessments.isLoading || history.isLoading) return <LoadingPane label="Loading exports" />;
  const assessmentById = new Map((assessments.data ?? []).map((a) => [a.id, a]));

  return (
    <div>
      <PageHeader
        title="Exports"
        subtitle="Generate audit-ready documents and evidence bundles (PLAN §20)"
      />

      <Card title="Generate an export">
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 260px' }}>
            <Field label="Assessment" required>
              {(props) => (
                <Select {...props} value={assessmentId} onChange={(e) => setAssessmentId(e.target.value)}>
                  <option value="">Choose an assessment…</option>
                  {(assessments.data ?? []).map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.name} ({a.versionLabel})
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <div style={{ flex: '1 1 260px' }}>
            <Field
              label="Format"
              hint={FORMAT_OPTIONS.find((f) => f.value === format)?.hint}
            >
              {(props) => (
                <Select {...props} value={format} onChange={(e) => setFormat(e.target.value as ExportFormat)}>
                  {FORMAT_OPTIONS.map((f) => (
                    <option key={f.value} value={f.value}>
                      {f.label}
                    </option>
                  ))}
                </Select>
              )}
            </Field>
          </div>
          <div style={{ marginBottom: 16 }}>
            <Button loading={generate.isPending} disabled={!assessmentId} onClick={() => generate.mutate()}>
              Generate
            </Button>
          </div>
        </div>
      </Card>

      <Card title="Export history">
        {(history.data?.items ?? []).length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)' }}>No exports generated yet.</p>
        ) : (
          <Table
            head={
              <>
                <th>Assessment</th>
                <th>Format</th>
                <th>Status</th>
                <th>Requested</th>
                <th />
              </>
            }
          >
            {(history.data?.items ?? []).map((e) => (
              <tr key={e.id}>
                <td>{assessmentById.get(e.assessmentId)?.name ?? e.assessmentId}</td>
                <td>
                  <Badge tone="primary">{e.format}</Badge>
                </td>
                <td>
                  <Badge tone={e.status === 'succeeded' ? 'success' : e.status === 'failed' ? 'danger' : 'info'}>
                    {e.status}
                  </Badge>
                  {e.error && (
                    <div style={{ color: 'var(--color-danger)', fontSize: 'var(--text-xs)' }}>{e.error}</div>
                  )}
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>{formatDateTime(e.createdAt)}</td>
                <td>
                  {e.status === 'succeeded' && (
                    <a href={apiUrl(`/exports/${e.id}/download`)}>
                      <Button size="sm" variant="secondary">
                        Download
                      </Button>
                    </a>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>
    </div>
  );
}
